import React from "react";
import PropTypes from "prop-types";
import Radium from "radium";
import {
  asNumberString,
  asPercentageString,
  asPercentageStringDecimal
} from "../../utils/formatters";
import AnimatedNumber from "react-animated-number";
import ReactTooltip from "react-tooltip";
import { handleTooltip } from "../../utils/handleToolTip";
import formatNumber from "../../utils/formatNumber";
import { currencySymbols, POSITION } from "../../utils/currency-position";
import SectionContainer from "../sectionContainer/sectionContainer";
import PercentChanged from "../percentChanged/percentChanged";
import numbro from "numbro";

function DataDisplay(props) {
  props = {
    url: "",
    toolTipContent: "",
    value: 23,
    size: 46,
    color: "#4a9bff",
    duration: props.disableAnimation ? 0 : 1000,
    isPercentage: false,
    showDecimal: false,
    toFixedValue: null,
    sectionProps: {},
    customValue: props.customValue,
    ...props
  };
  const style = {
    margin: 0,
    padding: 0,
    display: "inline",
    fontWeight: "100",
    transition: "0.8s ease-out",
    fontSize: props.size,
    color: props.color,
    ...props.customStyles
  };

  const formatValueNumber = (n) => {
    return asNumberString(n, props.value) + `${props.isPercentage ? "%" : ""}`;
  };

  const formatValuePercent = (n) =>
    props.showDecimal
      ? asPercentageStringDecimal(n, props.toFixedValue)
      : asPercentageString(n, props.value);

  const formatValueMoney = (n) => {
    let format = props.decimals ? "0,0.0" : "0,0";
    if (props.currency === "%" && props.value > 0 && props.value < 1) {
      format = "0.0%";
    }
    return formatNumber(n, format).replace("%", "");
  };

  const defaultValue = (n) => {
    return numbro(n).format({
      thousandSeparated: true
    });
  };
  const formatValue = (type) => {
    if (type === "money") {
      return formatValueMoney;
    } else if (type === "percentage") {
      return formatValuePercent;
    } else if (type === "default") {
      return defaultValue;
    } else {
      return formatValueNumber;
    }
  };
  const animatedProps = {
    style,
    component: "h5",
    value: 0 || props.value,
    initialValue: props.disableAnimation ? +props.value : 0,
    ["data-testid"]: props.testId,
    className: "moneyDisplay",
    duration: props.duration,
    stepPrecision: props.currency ? 1 : 0,
    formatValue: formatValue(props.type)
  };

  const renderCurrencySymbol = (position) => {
    const symbol = currencySymbols[props.currency];
    if (symbol === position || (!symbol && position === POSITION.START)) {
      return (
        <span style={style}>
          <sup style={{ fontSize: "60%" }}>{props.currency}</sup>
        </span>
      );
    }
  };

  const Display = () => (
    <>
      <span
        style={{ display: "inline-block" }}
        data-tip={
          props.type === "number" ? handleTooltip(props.toolTipContent) : props.toolTipContent
        }
        data-for={props.toolTipId}
        data-testid="dataDisplay"
      >
        {props.type === "money" && renderCurrencySymbol(POSITION.START)}
        {props.url !== "" ? (
          <a style={styles.cta} href={props.url} target="_raw_data_tab">
            <AnimatedNumber {...animatedProps} />
          </a>
        ) : props.customValue ? (
          <p style={style} data-testid={props.testId}>
            {props.customValue}
          </p>
        ) : (
          <AnimatedNumber {...animatedProps} />
        )}
        {props.type === "money" && renderCurrencySymbol(POSITION.END)}
        {props.toolTipId ? <ReactTooltip id={props.toolTipId} html /> : ""}
      </span>
      {props.changeProps && <PercentChanged {...props.changeProps} />}
      {props.averageProps && <Average {...props.averageProps} />}
    </>
  );

  return props.label ? (
    <SectionContainer title={props.label} {...props.sectionProps}>
      <div id="me">
        <Display />
      </div>
    </SectionContainer>
  ) : (
    <Display />
  );
}

DataDisplay.propTypes = {
  type: PropTypes.string,
  label: PropTypes.string,
  customStyles: PropTypes.object,
  changeProps: PropTypes.object,
  sectionProps: PropTypes.object,
  averageProps: PropTypes.object,
  // Number
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.number,
  color: PropTypes.string,
  url: PropTypes.string,
  disableAnimation: PropTypes.bool,
  duration: PropTypes.bool,
  isPercentage: PropTypes.bool,
  toolTipContent: PropTypes.string,
  toolTipId: PropTypes.string,
  // Percent
  showDecimal: PropTypes.bool,
  toFixedValue: PropTypes.number,
  // Money
  currency: PropTypes.string,
  testId: PropTypes.string,
  currencyLocation: PropTypes.string,
  decimals: PropTypes.number
};

let styles = {
  cta: {
    textDecoration: "none"
  }
};

// eslint-disable-next-line react/prop-types
function Average({ toolTip = "", fontSize = "16px", average, format = {}, symbol = "" }) {
  if (average == null) return null;

  let formattedVal = numbro(average).format({ thousandSeparated: true, ...format });

  return (
    <div
      style={{
        ...averageStyles.container,
        fontSize: fontSize
      }}
    >
      <span data-tip={toolTip} style={{ ...averageStyles.baseStyle, ...averageStyles.withBorder }}>
        {formattedVal}
        {symbol}
      </span>

      {toolTip ? <ReactTooltip /> : ""}
    </div>
  );
}

const averageStyles = {
  container: {
    display: "inline-block"
  },
  baseStyle: {
    padding: 5,
    fontWeight: "800",
    display: "flex",
    color: "#4a9bff"
  },
  withBorder: {
    border: "1px solid grey",
    borderRadius: 5,
    padding: "2px 10px 2px 10px",
    position: "relative",
    left: "10px",
    display: "inline",
    top: "-5px"
  }
};

export default Radium(DataDisplay);
